import { useState } from 'react';
import { Box, Typography, Button, Stepper, Step, StepLabel, Paper, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { PATHS } from '@/router/paths';
import useTeacherStore from '@/store/teacherStore';
import { useSchool } from '@/hooks/useSchool';

import { PersonalInfoStep } from './components/forms/PersonalInfoStep';
import { ProfessionalInfoStep } from './components/forms/ProfessionalInfoStep';
import { ContactInfoStep } from './components/forms/ContactInfoStep';
import { DocumentsStep } from './components/forms/DocumentsStep';
import { BankDetailsStep } from './components/forms/BankDetailsStep';
import { LoginCredentialsStep } from './components/forms/LoginCredentialsStep';

const steps = [
  'Personal Info', 
  'Professional Details', 
  'Contact Info', 
  'Documents', 
  'Bank Details',
  'Login Credentials'
];

// Combine validations for the current step
const validationSchemas = [
  Yup.object({
    firstName: Yup.string()
      .trim()
      .required('First name is required')
      .matches(/^[a-zA-Z\s'-]+$/, 'First name must contain only letters'),
    lastName: Yup.string()
      .trim()
      .required('Last name is required')
      .matches(/^[a-zA-Z\s'-]+$/, 'Last name must contain only letters'),
    gender: Yup.string().required('Gender is required'),
    dateOfBirth: Yup.date().required('Date of birth is required'),
  }),
  Yup.object({
    employeeId: Yup.string().required('Employee ID is required'),
    department: Yup.string()
      .trim()
      .required('Department is required')
      .matches(/^[a-zA-Z\s.,'-]+$/, 'Department must contain only letters'),
    designation: Yup.string()
      .trim()
      .required('Designation is required')
      .matches(/^[a-zA-Z\s.,'-]+$/, 'Designation must contain only letters'),
    joiningDate: Yup.date().required('Joining date is required'),
    qualification: Yup.string().required('Qualification is required'),
  }),
  Yup.object({
    phone: Yup.string()
      .required('Phone number is required')
      .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    address: Yup.string().required('Address is required'),
  }),
  Yup.object(), // Documents optional for mock
  Yup.object(), // Bank details optional
  Yup.object({
    loginEmail: Yup.string().email('Invalid email').required('Login email is required'),
    password: Yup.string().min(4, 'Password must be at least 4 characters').required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required'),
  }),
];

export function AddTeacherPage() {
  const navigate = useNavigate();
  const { schoolId } = useSchool();
  const createTeacher = useTeacherStore((s) => s.createTeacher);
  const loading = useTeacherStore((s) => s.loading);
  const [activeStep, setActiveStep] = useState(0);
  const isLastStep = activeStep === steps.length - 1;

  const initialValues = {
    firstName: '', lastName: '', gender: '', dateOfBirth: '', bloodGroup: '', aadhaar: '', pan: '',
    employeeId: '', joiningDate: '', department: '', designation: '', employmentType: 'Permanent', qualification: '', experience: '',
    phone: '', email: '', emergencyContact: '', address: '',
    photo: '', resume: '', certificates: '', idProof: '',
    bankDetails: { accountHolder: '', bankName: '', accountNumber: '', ifsc: '' },
    loginEmail: '', password: '', confirmPassword: ''
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchemas[activeStep],
    onSubmit: async (values, actions) => {
      if (isLastStep) {
        try {
          await createTeacher(schoolId, values);
          toast.success('Teacher added successfully');
          navigate(PATHS.TEACHERS);
        } catch (error) {
          toast.error(error.message || 'Failed to add teacher');
          actions.setSubmitting(false);
        }
      } else {
        setActiveStep((prev) => prev + 1);
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    },
  });

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0: return <PersonalInfoStep formik={formik} />;
      case 1: return <ProfessionalInfoStep formik={formik} />;
      case 2: return <ContactInfoStep formik={formik} />;
      case 3: return <DocumentsStep formik={formik} />;
      case 4: return <BankDetailsStep formik={formik} />;
      case 5: return <LoginCredentialsStep formik={formik} />;
      default: return <div>Not Found</div>;
    }
  };

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Add New Teacher
        </Typography>
        <Typography color="text.secondary">
          Register a new staff member to the system
        </Typography>
      </Box>

      <Paper sx={{ p: 4, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 6 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ minHeight: 300, mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0 || loading}
              onClick={handleBack}
              variant="outlined"
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || formik.isSubmitting}
            >
              {isLastStep ? 'Create Teacher' : 'Next Step'}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}
